import { paymentStatusLabel, paymentStatusOptions } from "helpers/paymentLifecycle";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "store/index";

import {
  getWithdrawalsList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  getWithdrawalStatusesList,
  modifyWithdrawalStatus,
} from "store/payments/withdrawals/slice";

import { withdrawalsListLimit, withdrawalsListLimits } from "configs/limits";

import Pagination from "components/Pagination";
import WithdrawalsFilters, {
  toIsoDateTime,
} from "./components/WithdrawalsFilters";

import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { handleApiError } from "helpers/handleApiError";
import { showNotification, NotificationTypes } from "store/notifications/slice";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissions } from "helpers/usePermission";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";


const compactReference = (value: string, startLength = 10, endLength = 8) => {
  if (value.length <= startLength + endLength + 3) return value;

  return `${value.slice(0, startLength)}...${value.slice(-endLength)}`;
};

const WithdrawalsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [currentLimit, setCurrentLimit] = useState(withdrawalsListLimit);

  const [order, setOrder] = useState({
    id: "none",
    userId: "none",
    amount: "none",
    currency: "none",
    paymentMethod: "none",
    status: "none",
    created: "none",
  });

  const [currencies, setCurrencies] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [userIds, setUserIds] = useState<number[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [externalRef, setExternalRef] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [completedFrom, setCompletedFrom] = useState("");
  const [completedTo, setCompletedTo] = useState("");
  const [statuses, setStatuses] = useState<{ value: string; label: string }[]>(
    [],
  );

  const list = useAppSelector((store) => store.paymentsWithdrawals.list);
  const isListFetched = useAppSelector(
    (store) => store.paymentsWithdrawals.isListFetched,
  );
  const totalItems = useAppSelector(
    (store) => store.paymentsWithdrawals.totalItems,
  );
  const totalPages = useAppSelector(
    (store) => store.paymentsWithdrawals.totalPages,
  );
  const activePage = useAppSelector(
    (store) => store.paymentsWithdrawals.activePage,
  );
  const selectedItems = useAppSelector(
    (store) => store.paymentsWithdrawals.selectedItems,
  );
  const statusesList = useAppSelector(
    (store) => store.paymentsWithdrawals.statusesList,
  );
  const processingItemId = useAppSelector(
    (store) => store.paymentsWithdrawals.processingItemId,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'paymentsWithdrawals',
    isListFetched
  });

  const activePermissions = usePermissions("payments_transactions_withdrawals");
  const canUpdate =
    activePermissions !== null && activePermissions.includes("update");



  useEffect(() => {
    dispatch(getWithdrawalStatusesList());
  }, []);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getWithdrawalsList({
          limit: currentLimit,
          offset: activePage - 1,

          ...(userIds.length && { userIds }),
          ...(currencies.length && {
            currencies: currencies.map((el) => el.value as string),
          }),

          ...(statuses.length && {
            canonical_status: statuses.map((el) => el.value as string),
          }),

          ...(paymentMethod && { paymentMethod }),
          ...(externalRef && { externalRef }),
          ...(createdFrom && { createdAtFrom: toIsoDateTime(createdFrom) }),
          ...(createdTo && { createdAtTo: toIsoDateTime(createdTo) }),
          ...(completedFrom && {
            completedAtFrom: toIsoDateTime(completedFrom),
          }),
          ...(completedTo && { completedAtTo: toIsoDateTime(completedTo) }),

          order,
        }),
      );
    }
  }, [currentLimit, dispatch, isListFetched]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };

  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      ...Object.fromEntries(Object.entries(order).map(([k]) => [k, "none"])),
      [key]: next,
    };

    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

const formattedList = list.map((item) => ({
  id: item.id,
  userId: item.userId,

  requestedAmount: `${item.amount} ${item.currency}`,

  payoutAmount: `${item.destinationAmount ?? item.amount} ${
    item.destinationCurrency ?? item.currency
  }`,

  provider: {
    title: item.paymentProviderTitle ?? "—",
  },

  method: `${item.paymentMethodTitle ?? item.paymentMethod ?? "—"}${item.origin === "backoffice_manual" ? ` · ${t("manual")}` : ""}`,

  methodImage: item.paymentMethodImageCdn ?? "",

  externalRef: item.externalRef || "—",
  destination: item.destination || "—",

  status: paymentStatusLabel(item, statusesList),
    canonical_status: item.canonical_status,
    allowed_status_actions: item.allowed_status_actions,

  created: item.created,
  completed: item.completedAt || "—",

  processingType: item.processingType,
}));

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <WithdrawalsFilters
        selectedCurrencies={currencies}
        handleUpdateCurrencies={(e) => setCurrencies(e)}
        selectedStatuses={statuses}
        handleUpdateStatuses={(e) =>
          setStatuses(
            e.map((el) => ({
              value: String(el.value),
              label: el.label,
            })),
          )
        }
        handleUpdateUserIds={(ids) => setUserIds(ids)}
        handleUpdatePaymentMethod={(val) => setPaymentMethod(val)}
        externalRef={externalRef}
        setExternalRef={setExternalRef}
        createdFrom={createdFrom}
        createdTo={createdTo}
        setCreatedRange={(from, to) => {
          setCreatedFrom(from);
          setCreatedTo(to);
        }}
        completedFrom={completedFrom}
        completedTo={completedTo}
        setCompletedRange={(from, to) => {
          setCompletedFrom(from);
          setCompletedTo(to);
        }}
        handleApply={handleApply}
      />

      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={10} rowsCount={12} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap table">
            {list.length === 0 && (
              <EmptyState message={t("noWithdrawalTransactionsFound")} />
            )}

            {list.length > 0 && (
              <Table
                data={formattedList}
                idField="id"
                order={order}
                selectedItems={selectedItems}
                onAllSelect={handleAllClick}
                onSelect={(id) => handleItemClick(id)}
                onSort={(id) => (id ? handleOrderClick(id) : {})}
                hasCheckbox={false}
                hasOptions={false}
                processingId={processingItemId}
                columns={{
                  hiddenColumns: [
                    "processingType",
                    "methodImage",
                    "canonical_status",
                    "allowed_status_actions",
                  ],

                  sortingColumns: {
                    id: {
                      value: "ID",
                      hasSorting: true,
                    },

                    userId: {
                      value: "User ID",
                      hasSorting: true,
                    },

                    requestedAmount: {
                      value: "Requested amount",
                    },

                    payoutAmount: {
                      value: "Payout amount",
                    },

                    provider: {
                      value: "Provider",
                    },

                    method: {
                      value: "Method",
                    },

                    externalRef: {
                      value: "External Ref",
                    },

                    destination: {
                      value: "Destination",
                    },

                    status: {
                      value: "Status",
                      hasSorting: true,
                    },

                    created: {
                      value: "Created",
                      hasSorting: true,
                    },

                    completed: {
                      value: "Completed",
                    },
                  },
                }}
                customColumns={(row) => {
                  const statusOptions = paymentStatusOptions(row, statusesList);
                  const isDisabled = !canUpdate || !row.allowed_status_actions?.length;

                  return {
                    id: (
                      <div className="copyable-title-cell">
                        <CopyToClipboard text={String(row.id)} />
                        <span title={String(row.id)}>{row.id}</span>
                      </div>
                    ),
                    externalRef: (
                      <div className="copyable-title-cell">
                        {row.externalRef !== "—" && (
                          <CopyToClipboard text={row.externalRef} />
                        )}
                        <span title={row.externalRef}>
                          {compactReference(row.externalRef)}
                        </span>
                      </div>
                    ),
                    destination: (
                      <div className="copyable-title-cell">
                        {row.destination !== "—" && (
                          <CopyToClipboard text={row.destination} />
                        )}
                        <span title={row.destination}>
                          {compactReference(row.destination, 6, 4)}
                        </span>
                      </div>
                    ),
                    method: (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {row.methodImage && (
                          <img
                            src={row.methodImage}
                            alt={row.method}
                            style={{
                              width: 20,
                              height: 20,
                              objectFit: "contain",
                            }}
                          />
                        )}

                        <span>{row.method}</span>
                      </div>
                    ),
                    status: (
                      <div
                        className={`table-status-dropdown table-status-dropdown--withdrawals table-status-dropdown--${row.status?.toLowerCase()}`}
                      >
                        <DefaultDropdown
                          value={row.canonical_status}
                          selectedLabel={row.status}
                          options={statusOptions}
                          disabled={isDisabled}
                          isSmall
                          onSelect={async (value) => {
                            if (!canUpdate || !row.allowed_status_actions?.includes(String(value))) return;

                            if (value === row.canonical_status)
                              return;

                            try {
                              await dispatch(
                                modifyWithdrawalStatus({
                                  id: row.id,
                                  statusCode: String(value),
                                }),
                              ).unwrap();

                              dispatch(
                                getWithdrawalsList({
                                  limit: currentLimit,
                                  offset: activePage - 1,
                                  ...(userIds.length && { userIds }),
                                  ...(currencies.length && {
                                    currencies: currencies.map(
                                      (el) => el.value as string,
                                    ),
                                  }),
                                  ...(statuses.length && {
                                    canonical_status: statuses.map(
                                      (el) => el.value as string,
                                    ),
                                  }),
                                  ...(paymentMethod && { paymentMethod }),
                                  ...(externalRef && { externalRef }),
                                  ...(createdFrom && {
                                    createdAtFrom: toIsoDateTime(createdFrom),
                                  }),
                                  ...(createdTo && {
                                    createdAtTo: toIsoDateTime(createdTo),
                                  }),
                                  ...(completedFrom && {
                                    completedAtFrom:
                                      toIsoDateTime(completedFrom),
                                  }),
                                  ...(completedTo && {
                                    completedAtTo: toIsoDateTime(completedTo),
                                  }),
                                  order,
                                }),
                              );

                              dispatch(
                                showNotification({
                                  title: t("success"),
                                  message: t("entityUpdateSuccess"),
                                  type: NotificationTypes.success,
                                }),
                              );
                            } catch (error) {
                              handleApiError({
                                error,
                                t,
                                dispatch,
                              });
                            }
                          }}
                        />
                      </div>
                    ),
                  };
                }}
              />
            )}
          </div>
        )}

        {isListFetched && list.length > 0 && (
          <Pagination
            limitsArray={withdrawalsListLimits}
            handleLimitClick={(limit) => {
              setCurrentLimit(limit);
              dispatch(handlePaginate(1));
            }}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default WithdrawalsList;
